import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  ViewChild,
  Input,
  SimpleChanges,
  OnChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'

import {
  ReactiveFormsModule,
  FormBuilder,
  FormsModule,
  Validators,
  FormArray,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatIcon } from '@angular/material/icon'
import { MatChipsModule } from '@angular/material/chips'
import { MatDivider } from '@angular/material/divider'
import { MatButtonModule } from '@angular/material/button'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectModule } from '@angular/material/select'
import { ROUTES } from '../../../constants/routes'
import { RouteService } from '../../../services/route.service'
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { debounceTime, firstValueFrom, Subject, switchMap } from 'rxjs'
import {
  ComponentAutoCompleteResult,
  ComponentFormData,
  ComponentFormRow,
  ComponentGroupFormRow,
} from '../../../types/components'
import { ComponentService } from '../../../services/component.service'
import { TranslationService } from '../../../services/translation.service'
@Component({
  selector: 'app-product-components-form',
  imports: [
    MatFormField,
    MatLabel,
    MatInputModule,
    ReactiveFormsModule,
    MatIcon,
    MatChipsModule,
    MatDivider,
    MatButtonModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    FormsModule,
    MatDivider,
    MatRadioModule,
  ],
  templateUrl: './product-components-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-components-form.component.scss',
})
export class ProductComponentsFormComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder)
  private componentService = inject(ComponentService)
  private routeService = inject(RouteService)
  translationService = inject(TranslationService)

  @Input() clonedData: ComponentFormData | undefined
  @Output() formChanged = new EventEmitter<ComponentFormData>()
  @Output() backPressed = new EventEmitter()
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger

  componentQueryChanged: Subject<{
    query: string
    groupIndex: number
    componentIndex: number
  }> = new Subject<{
    query: string
    groupIndex: number
    componentIndex: number
  }>()
  ungroupedComponentQueryChanged: Subject<{
    query: string
    componentIndex: number
  }> = new Subject<{
    query: string
    componentIndex: number
  }>()

  filteredOptions: ComponentAutoCompleteResult[][][] = []
  filteredUngroupedOptions: ComponentAutoCompleteResult[][] = []

  componentForm = this.fb.group({
    components: this.fb.array([]),
    componentGroups: this.fb.array([]),
  })

  get ungroupedComponents(): FormArray {
    return this.componentForm.get('components') as FormArray
  }

  getUngroupedComponents(): FormArray {
    return this.ungroupedComponents
  }

  get componentGroups(): FormArray {
    return this.componentForm.get('componentGroups') as FormArray
  }

  getComponentsForGroup(groupIndex: number): FormArray {
    return this.componentGroups.at(groupIndex).get('components') as FormArray
  }

  ngOnInit(): void {
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.COMPANY_PRODUCTS.title,
      `/admin/companies/${ROUTES.COMPANY_PRODUCTS.path}`,
    )
    this.routeService.setRoute(currentRoute)
    this.componentQueryChanged
      .pipe(
        debounceTime(300),
        switchMap(async ({ query, groupIndex, componentIndex }) => {
          const name = query
          try {
            if (
              (typeof name === 'object' && name !== null) ||
              (name !== null && name.length < 3)
            ) {
              return {
                results: [],
                groupIndex,
                componentIndex,
              }
            }
            const results: Array<ComponentAutoCompleteResult> | undefined =
              await firstValueFrom(
                this.componentService.autoCompleteComponents(name),
              )

            if (results == null || results.length === 0) {
              const msg = [
                {
                  id: 0,
                  displayName:
                    this.translationService.getNoComponentsFoundText(),
                  name: this.translationService.getNoComponentsFoundText(),
                  measureUnit: null,
                  measureValue: null,
                  supplier: null,
                },
              ]
              return { results: msg, groupIndex, componentIndex }
            } else {
              return { results, groupIndex, componentIndex }
            }
          } catch {
            const msg = [
              {
                id: 0,
                displayName: this.translationService.getNoComponentsFoundText(),
                name: this.translationService.getNoComponentsFoundText(),
                measureUnit: null,
                measureValue: null,
                supplier: null,
              },
            ]
            return { results: msg, groupIndex, componentIndex }
          }
        }),
      )
      .subscribe((res) => {
        const { groupIndex, componentIndex, results } = res
        if (!this.filteredOptions[groupIndex]) {
          this.filteredOptions[groupIndex] = []
        }
        this.filteredOptions[groupIndex][componentIndex] = results.map(
          (element) => {
            return {
              id: element.id,
              name: element.name,
              measureUnit: element.measureUnit,
              measureValue: element.measureValue,
              supplier: element.supplier ?? null,
            } as ComponentAutoCompleteResult
          },
        )
      })

    this.ungroupedComponentQueryChanged
      .pipe(
        debounceTime(300),
        switchMap(async ({ query, componentIndex }) => {
          const name = query
          try {
            if (
              (typeof name === 'object' && name !== null) ||
              (name !== null && name.length < 3)
            ) {
              return {
                results: [],
                componentIndex,
              }
            }

            const results: Array<ComponentAutoCompleteResult> | undefined =
              await firstValueFrom(
                this.componentService.autoCompleteComponents(name),
              )

            if (results == null || results.length === 0) {
              const msg = [
                {
                  id: 0,
                  displayName:
                    this.translationService.getNoComponentsFoundText(),
                  name: this.translationService.getNoComponentsFoundText(),
                  measureUnit: null,
                  measureValue: null,
                  supplier: null,
                },
              ]
              return { results: msg, componentIndex }
            } else {
              return { results, componentIndex }
            }
          } catch {
            const msg = [
              {
                id: 0,
                displayName: this.translationService.getNoComponentsFoundText(),
                name: this.translationService.getNoComponentsFoundText(),
                measureUnit: null,
                measureValue: null,
                supplier: null,
              },
            ]
            return { results: msg, componentIndex }
          }
        }),
      )
      .subscribe((res) => {
        const { componentIndex, results } = res
        this.filteredUngroupedOptions[componentIndex] = results.map(
          (element) => {
            return {
              id: element.id,
              name: element.name,
              measureUnit: element.measureUnit,
              measureValue: element.measureValue,
              supplier: element.supplier ?? null,
            } as ComponentAutoCompleteResult
          },
        )
      })

    this.initializeComponentGroups()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clonedData']) {
      this.initializeComponentGroups()
    }
  }

  private initializeComponentGroups(): void {
    // clear existing groups and ungrouped components and options
    this.componentGroups.clear()
    this.ungroupedComponents.clear()
    this.filteredOptions = []
    this.filteredUngroupedOptions = []

    // when cloning, restore ungrouped components
    if (
      this.clonedData &&
      this.clonedData.components &&
      this.clonedData.components.length > 0
    ) {
      this.clonedData.components.forEach((component) => {
        this.cloneUngroupedComponentRow(component)
      })
    }

    // when cloning, restore component groups and their components
    if (
      this.clonedData &&
      this.clonedData.componentGroups &&
      this.clonedData.componentGroups.length > 0
    ) {
      this.clonedData.componentGroups.forEach((group) => {
        const groupIndex = this.addComponentGroup(false, group.name)
        if (group.components && group.components.length > 0) {
          group.components.forEach((component) => {
            this.cloneComponentRow(groupIndex, component)
          })
        }
      })
    }
  }

  private minimumComponentsInGroupValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const components = group.get('components') as FormArray | null
      if (!components) {
        return null
      }

      const length = components.length
      // Allow groups with zero components (user may still be editing)
      // Enforce minimum of 2 once there is at least one component.
      if (length === 0 || length >= 2) {
        return null
      }

      return { minGroupSize: true }
    }
  }

  private createEmptyGroup(): FormGroup {
    const componentsArray = this.fb.array([])

    return this.fb.group(
      {
        name: ['', [Validators.required, Validators.maxLength(36)]],
        components: componentsArray,
      },
      {
        validators: [this.minimumComponentsInGroupValidator()],
      },
    )
  }

  private createComponentRow(component?: ComponentFormRow): FormGroup {
    return this.fb.group({
      name: [component?.name || '', Validators.required],
      measureUnit: [component?.measureUnit || null],
      measureValue: [component?.measureValue || null],
      quantity: [
        component?.quantity ?? 1,
        [Validators.required, Validators.pattern(/^[1-9]\d*$/)],
      ],
      id: [component?.id || null, Validators.required],
      supplier: [{ value: component?.supplier || '', disabled: true }],
    })
  }

  addComponentGroup(
    seedInitialRow: boolean = true,
    name?: string | null,
  ): number {
    const group = this.createEmptyGroup()

    if (name !== undefined && name !== null) {
      group.get('name')?.setValue(name)
    }

    this.componentGroups.push(group)
    this.filteredOptions.push([])

    const newIndex = this.componentGroups.length - 1
    if (seedInitialRow) {
      this.addComponentRow(newIndex)
    }
    return newIndex
  }

  cloneComponentRow(groupIndex: number, component?: ComponentFormRow) {
    const groupComponents = this.getComponentsForGroup(groupIndex)
    const componentRow = this.createComponentRow(component)
    groupComponents.push(componentRow)

    if (!this.filteredOptions[groupIndex]) {
      this.filteredOptions[groupIndex] = []
    }
    this.filteredOptions[groupIndex].push([])
  }

  addComponentRow(groupIndex: number) {
    const groupComponents = this.getComponentsForGroup(groupIndex)
    const componentRow = this.createComponentRow()
    groupComponents.push(componentRow)

    if (!this.filteredOptions[groupIndex]) {
      this.filteredOptions[groupIndex] = []
    }
    this.filteredOptions[groupIndex].push([])
  }

  public removeComponent(groupIndex: number, componentIndex: number) {
    const groupComponents = this.getComponentsForGroup(groupIndex)

    if (this.filteredOptions[groupIndex]) {
      this.filteredOptions[groupIndex] = this.filteredOptions[
        groupIndex
      ].filter((_, idx) => idx !== componentIndex)
    }
    groupComponents.removeAt(componentIndex)
  }

  removeComponentGroup(groupIndex: number) {
    this.componentGroups.removeAt(groupIndex)
    this.filteredOptions.splice(groupIndex, 1)
  }

  private applySelectedComponentToGroupControl(
    selectedId: string,
    groupIndex: number,
    componentIndex: number,
  ): void {
    const optionsForRow =
      this.filteredOptions[groupIndex]?.[componentIndex] ?? []

    const match = optionsForRow.find((option) => option.id === selectedId)
    if (!match) {
      return
    }

    const groupComponents = this.getComponentsForGroup(groupIndex)
    const supplier = match.supplier ?? ''

    // Update the selected component row
    groupComponents.at(componentIndex).patchValue({
      id: match.id,
      name: match.name,
      measureUnit: match.measureUnit,
      measureValue: match.measureValue,
      supplier,
    })
  }

  private applySelectedComponentToUngroupedControl(
    selectedId: string,
    componentIndex: number,
  ): void {
    const optionsForRow = this.filteredUngroupedOptions[componentIndex] ?? []

    const match = optionsForRow.find((option) => option.id === selectedId)
    if (!match) {
      return
    }

    const ungrouped = this.getUngroupedComponents()
    const supplier = match.supplier ?? ''

    ungrouped.at(componentIndex).patchValue({
      id: match.id,
      name: match.name,
      measureUnit: match.measureUnit,
      measureValue: match.measureValue,
      supplier,
    })
  }

  onSelectionChanged(
    event: MatAutocompleteSelectedEvent,
    groupIndex: number,
    componentIndex: number,
  ): void {
    const selectedId = event.option.value as string
    if (!selectedId) {
      return
    }

    this.applySelectedComponentToGroupControl(
      selectedId,
      groupIndex,
      componentIndex,
    )
  }

  async onComponentChange(
    event: KeyboardEvent,
    groupIndex: number,
    componentIndex: number,
  ) {
    const element = event.currentTarget as HTMLInputElement
    const query = element.value

    const groupComponents = this.getComponentsForGroup(groupIndex)
    groupComponents.at(componentIndex).patchValue({
      id: null,
    })

    this.componentQueryChanged.next({ query, groupIndex, componentIndex })
  }

  addUngroupedComponentRow(): void {
    const ungrouped = this.getUngroupedComponents()
    const componentRow = this.createComponentRow()
    ungrouped.push(componentRow)
    this.filteredUngroupedOptions.push([])
  }

  cloneUngroupedComponentRow(component?: ComponentFormRow): void {
    const ungrouped = this.getUngroupedComponents()
    const componentRow = this.createComponentRow(component)
    ungrouped.push(componentRow)
    this.filteredUngroupedOptions.push([])
  }

  removeUngroupedComponent(componentIndex: number): void {
    const ungrouped = this.getUngroupedComponents()
    if (componentIndex < 0 || componentIndex >= ungrouped.length) {
      return
    }
    ungrouped.removeAt(componentIndex)
    this.filteredUngroupedOptions = this.filteredUngroupedOptions.filter(
      (_opts, idx) => idx !== componentIndex,
    )
  }

  onUngroupedSelectionChanged(
    event: MatAutocompleteSelectedEvent,
    componentIndex: number,
  ): void {
    const selectedId = event.option.value as string
    if (!selectedId) {
      return
    }

    this.applySelectedComponentToUngroupedControl(selectedId, componentIndex)
  }

  async onUngroupedComponentChange(
    event: KeyboardEvent,
    componentIndex: number,
  ): Promise<void> {
    const element = event.currentTarget as HTMLInputElement
    const query = element.value

    const ungrouped = this.getUngroupedComponents()
    ungrouped.at(componentIndex).patchValue({
      id: null,
    })

    this.ungroupedComponentQueryChanged.next({ query, componentIndex })
  }

  getUngroupedComponentWeightAndUnit(componentIndex: number): string {
    const ungrouped = this.getUngroupedComponents()
    const weight = ungrouped.at(componentIndex).get('measureValue')?.value
    const unit = ungrouped.at(componentIndex).get('measureUnit')?.value
    return `${weight != null ? weight : ''} ${unit != null ? unit : ''}`
  }

  onReset() {
    this.componentForm.reset()
    this.initializeComponentGroups()
  }

  goBack() {
    this.backPressed.emit(null)
  }

  getComponentFormAsFormGroup(groupIndex: number, componentIndex: number) {
    return this.getComponentsForGroup(groupIndex).at(
      componentIndex,
    ) as FormGroup
  }

  getComponentWeightAndUnit(groupIndex: number, componentIndex: number) {
    const groupComponents = this.getComponentsForGroup(groupIndex)
    const weight = groupComponents.at(componentIndex).get('measureValue')?.value
    const unit = groupComponents.at(componentIndex).get('measureUnit')?.value
    return `${weight != null ? weight : ''} ${unit != null ? unit : ''}`
  }

  async onSubmit() {
    this.componentForm.markAllAsTouched()
    if (!this.componentForm.valid) {
      return
    }

    const ungroupedComponents: ComponentFormRow[] = []
    const componentGroupsPayload: ComponentGroupFormRow[] = []

    // Build ungrouped components payload from the top-level components array
    this.getUngroupedComponents().controls.forEach((ctrl) => {
      const value = ctrl.value as {
        name: string
        measureUnit: string | null
        measureValue: number | null
        quantity: number
        id: string
        supplier?: string
      }

      const row: ComponentFormRow = {
        name: value.name,
        measureUnit: value.measureUnit ?? '',
        measureValue: value.measureValue ?? 0,
        quantity: value.quantity,
        id: value.id ?? '',
        supplier: value.supplier ?? '',
        // Ungrouped components are always present; they have no groupIndex.
        groupIndex: undefined,
      }

      ungroupedComponents.push(row)
    })

    // Build grouped components payload
    this.componentGroups.controls.forEach((groupControl, groupIndex) => {
      const groupComponentsArray = groupControl.get('components') as FormArray
      const groupComponents: ComponentFormRow[] = []

      groupComponentsArray.controls.forEach((ctrl) => {
        const value = ctrl.value as {
          name: string
          measureUnit: string | null
          measureValue: number | null
          quantity: number
          id: string
          supplier?: string
        }

        const row: ComponentFormRow = {
          name: value.name,
          measureUnit: value.measureUnit ?? '',
          measureValue: value.measureValue ?? 0,
          quantity: value.quantity,
          id: value.id,
          supplier: value.supplier ?? '',
          groupIndex,
        }

        groupComponents.push(row)
      })

      if (groupComponents.length > 0) {
        const groupName = (groupControl.get('name')?.value as string) ?? ''
        componentGroupsPayload.push({
          name: groupName,
          components: groupComponents,
        })
      }
    })

    const payload: ComponentFormData = {
      formName: 'componentForm',
      // "components" represents the ungrouped components only
      components: ungroupedComponents,
      componentGroups: componentGroupsPayload,
    }

    this.formChanged.emit(payload)
  }

  hasMinGroupSizeError(groupIndex: number): boolean {
    const group = this.componentGroups.at(groupIndex) as FormGroup | null
    if (!group) {
      return false
    }
    return group.touched && group.hasError('minGroupSize')
  }
}
